'use client'

import { LoginForm } from '@/components/auth/login-form'
import { RegisterForm } from '@/components/auth/register-form'
import { ModalInfoX2CoolCash } from '@/components/common/coolclub/modal-info-x2-coolcash'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Card } from '@/components/ui/card'
import { useCoolClubMemberQuery } from '@/hooks/react-query/club/use-club'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, formatNumber } from '@/lib/utils'
import { showPopup } from '@/providers/popup-provider'
import Image from 'next/image'
import { useMemo, useState } from 'react'

interface ProductInfoCoolcashRewardProps {
  regularPrice: number
  isDoubleDay?: boolean
  checkX2CoolCash?: {
    check_id: string
    check_time: string
    text_time: string
  }
}

type RankingId =
  | '64ea032b9e826a5344bce9a9'
  | '64ea041d9e826a5344bce9b3'
  | '64ea04359e826a5344bce9b4'
  | '64ea04499e826a5344bce9b5'

interface RankingConfig {
  percent: number
  image: string
}

const RANKING_PERCENT: Record<RankingId, RankingConfig> = {
  '64ea032b9e826a5344bce9a9': {
    percent: 0.01,
    image: '/image/November2024/mceclip3_95.png',
  },
  '64ea041d9e826a5344bce9b3': {
    percent: 0.03,
    image: '/image/November2024/mceclip2_43.png',
  },
  '64ea04359e826a5344bce9b4': {
    percent: 0.05,
    image: '/image/November2024/mceclip1_77.png',
  },
  '64ea04499e826a5344bce9b5': {
    percent: 0.07,
    image: '/image/November2024/mceclip0_84.png',
  },
}

export const ProductInfoCoolcashReward = ({
  regularPrice,
  isDoubleDay,
  checkX2CoolCash,
}: ProductInfoCoolcashRewardProps) => {
  const { data: userInfo } = useCoolClubMemberQuery()
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const defaultRankingId: RankingId = '64ea04499e826a5344bce9b5'
  const userRanking = (userInfo?.ranking?._id as RankingId) || defaultRankingId
  const userLoggedIn = !!userInfo
  const coolcashReward =
    Math.ceil((regularPrice * RANKING_PERCENT[userRanking].percent) / 1000) *
    1000
  const finalCoolcashReward = isDoubleDay ? coolcashReward * 2 : coolcashReward
  const rankingImage = RANKING_PERCENT[userRanking].image

  const isFriday = useMemo(() => {
    const today = new Date()
    return today.getDay() === 5 // 5 là thứ 5 trong tuần
  }, [])

  if (finalCoolcashReward <= 0) return null

  const openLoginPopup = () => {
    //đóng accordion

    showPopup(<LoginForm />)
  }

  const openRegisterPopup = () => {
    showPopup(<RegisterForm />)
  }

  const isX2CoolCash =
    (checkX2CoolCash?.check_id && checkX2CoolCash?.check_time) ||
    isDoubleDay ||
    isFriday

  return (
    <div
      className={cn(
        'mx-auto mb-4 w-full rounded-lg bg-[#F1F3FF]',
        isX2CoolCash && 'px-5 py-3'
      )}
    >
      {isX2CoolCash && (
        <div className='mb-2 flex items-center gap-1 text-sm'>
          {checkX2CoolCash?.check_id && checkX2CoolCash?.check_time && (
            <div className='text-black'>
              Hoàn tiền X2 Coolcash đến ngày {checkX2CoolCash.text_time}.
            </div>
          )}
          {(isDoubleDay || isFriday) && (
            <div className='text-black'>Hoàn tiền X2 Coolcash hôm nay.</div>
          )}
          <div
            onClick={() => showPopup(<ModalInfoX2CoolCash />)}
            className='font-semibold text-primary'
          >
            Tìm hiểu thêm
          </div>
        </div>
      )}

      <Card
        className={cn(
          'rounded-lg border-0 px-3 shadow-none',
          isX2CoolCash ? 'bg-[#D9DEFF]' : 'bg-[#F1F3FF]'
        )}
      >
        <Accordion
          type='single'
          collapsible
          onValueChange={(value) =>
            setIsAccordionOpen(value === 'coolcash-details')
          }
          value={isAccordionOpen ? 'coolcash-details' : ''}
        >
          <AccordionItem value='coolcash-details' className='border-0'>
            <AccordionTrigger className='flex items-center justify-between py-2 font-sans hover:no-underline'>
              <div className='flex items-center gap-2'>
                <Image
                  src='/icons/icon-coolcash-box.svg'
                  alt='Coolcash'
                  width={16}
                  height={16}
                  unoptimized
                  className='h-4 w-4'
                />
                <div className='text-sm font-normal'>
                  Được hoàn{!userLoggedIn && ' lên đến'}{' '}
                  <span className='font-medium'>
                    {formatNumber(finalCoolcashReward)}
                  </span>{' '}
                  CoolCash.
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className='pb-0 text-left'>
              <div className='border-t border-neutral-600/30 p-2 pb-2 pt-4'>
                <p className='text-black'>
                  Đây là số CoolCash ước tính bạn sẽ được hoàn lại khi mua sản
                  phẩm hôm nay, tương ứng với quyền lợi hạng{' '}
                  <Image
                    src={ImageHelper.getImageUrl(rankingImage)}
                    alt='Coolcash'
                    width={80}
                    height={16}
                    className='inline h-5 w-auto object-contain'
                    unoptimized
                  />
                </p>

                {!userLoggedIn && (
                  <>
                    <p className='mb-1 mt-4 text-black'>
                      CoolCash có giá trị như tiền mặt dùng để mua hàng tại
                      website Coolmate.me
                    </p>
                    <div>
                      <button
                        onClick={openLoginPopup}
                        className='mr-2 font-medium text-primary'
                      >
                        Đăng nhập
                      </button>
                      hoặc
                      <button
                        onClick={openRegisterPopup}
                        className='ml-2 font-medium text-primary'
                      >
                        Đăng ký
                      </button>
                      <span className='font-normal text-black'>
                        {' '}
                        ngay để kiểm tra mức hoàn tiền chính xác nhất dành cho
                        bạn.
                      </span>
                    </div>
                  </>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>
    </div>
  )
}
